import { crmOptionService } from '../../helpers/services/crmOption.service'
import { sharedHelper } from '@/utils'

/**
 * CRM dropdown vocabularies (lead status/source, industries, deal stages, lost
 * reasons, activity types) served by /v1/crm/options. Fetched once per session
 * and cached here, so a status added in configurations shows up everywhere
 * without a frontend change.
 */
export const state = {
  salesTaxRate: 0,
  options: {},
  loaded: false,
}

export const CRM_OPTION_MODULE = 'crmOption'

export const GET_CRM_OPTIONS = 'getCrmOptions'
export const ADD_CRM_OPTION = 'addCrmOption'

const OPTIONS_SUCCESS = 'optionsSuccess'

// Used until the API answers (and if the call fails) so a form is never empty.
const FALLBACK = {
  lead_statuses: [
    { value: 'new', label: 'New', color: '#3D5AFE' },
    { value: 'contacted', label: 'Contacted', color: '#F59E0B' },
    { value: 'qualified', label: 'Qualified', color: '#7C4DFF' },
    { value: 'converted', label: 'Converted', color: '#10B981', terminal: true },
    { value: 'lost', label: 'Lost', color: '#8993A4', terminal: true },
  ],
  lead_sources: [],
  industries: [],
  deal_stages: [
    { value: 'prospecting', label: 'Prospecting', color: '#3D5AFE' },
    { value: 'qualification', label: 'Qualification', color: '#00BCD4' },
    { value: 'proposal', label: 'Proposal', color: '#7C4DFF' },
    { value: 'negotiation', label: 'Negotiation', color: '#F59E0B' },
    { value: 'closed_won', label: 'Won', color: '#10B981', terminal: true },
    { value: 'closed_lost', label: 'Lost', color: '#8993A4', terminal: true },
  ],
  lost_reasons: [],
  activity_types: [],
}

export const actions = {
  /**
   * Adds a vocabulary value and refreshes, so the caller can select it straight
   * away. Forced, because the cached copy is what every picker reads from.
   */
  [ADD_CRM_OPTION]({ dispatch }, { group, label }) {
    return crmOptionService.addValue(group, label).then(async (r) => {
      await dispatch(GET_CRM_OPTIONS, { force: true })
      return r?.data || null
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
      return null
    })
  },
  [GET_CRM_OPTIONS]({ commit, state }, { force = false } = {}) {
    if (state.loaded && !force) return Promise.resolve(state.options)
    return crmOptionService.getAll().then((r) => {
      commit(OPTIONS_SUCCESS, r?.data || {})
      return r?.data || {}
    }).catch(() => {
      // Options are cosmetic; fall back rather than blocking the page.
      commit(OPTIONS_SUCCESS, FALLBACK)
      return FALLBACK
    })
  },
}

export const mutations = {
  [OPTIONS_SUCCESS](state, options) {
    // Rides along with the vocabularies: the deal form quotes with tax and
    // needs the same rate the AutoCount document will use.
    state.salesTaxRate = Number(options.sales_tax_rate) || 0
    state.options = { ...FALLBACK, ...options }
    state.loaded = true
  },
}

export const getters = {
  group: (state) => (name) => state.options[name] || FALLBACK[name] || [],
  /** Options a user may pick in a form — terminal ones are endpoint-driven. */
  selectable: (state) => (name) =>
    (state.options[name] || FALLBACK[name] || []).filter((o) => !o.terminal),
  labelOf: (state) => (name, value) => {
    const found = (state.options[name] || FALLBACK[name] || []).find((o) => o.value === value)
    return found ? found.label : value
  },
  colorOf: (state) => (name, value) => {
    const found = (state.options[name] || FALLBACK[name] || []).find((o) => o.value === value)
    return found && found.color ? found.color : '#8993A4'
  },
}
